import { Injectable } from '@angular/core';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
} from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Snapshot of the user's current page, captured at every NavigationEnd and
 * sent to Gable on each chat turn so the assistant can answer "what am I
 * looking at?" without per-page wiring.
 *
 * Meta, not per-page: the service introspects the activated route to derive
 * `componentName` / `params` / `title` automatically. Pages don't have to
 * push anything; if a page wants to override or enrich the snapshot it can
 * call `set()`, but the default path is route-driven.
 */
export type PageSelectionEntity =
    | 'units' | 'members' | 'charges' | 'violations' | 'requests'
    | 'payables' | 'transactions' | 'vendors' | 'documents' | 'tasks';

export interface PageSelection {
    entity: PageSelectionEntity;
    ids: number[];
    count: number;
    all: boolean;
}

export const PAGE_SELECTION_MAX_IDS = 5000;

export interface PageContext {
    url: string;
    selection?: PageSelection;
    componentName?: string;
    params?: Record<string, string>;
    title?: string;
}

@Injectable({ providedIn: 'root' })
export class PageContextService {
    private readonly _context$ = new BehaviorSubject<PageContext | null>(null);
    private _selection: PageSelection | null = null;

    constructor(
        private readonly _router: Router,
        private readonly _route: ActivatedRoute,
    ) {
        this._router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((event) => {
                this._selection = null;
                this._context$.next(this._buildContext(event.urlAfterRedirects));
            });
    }

    /** Synchronous read for callers that want the current snapshot (e.g. chat). */
    snapshot(): PageContext | null {
        const ctx = this._context$.value;
        if (!ctx) { return ctx; }
        return this._selection ? { ...ctx, selection: this._selection } : ctx;
    }

    setSelection(sel: PageSelection | null): void {
        if (!sel || (sel.count <= 0 && !sel.all)) {
            this._selection = null;
            return;
        }
        this._selection = { ...sel, ids: sel.ids.slice(0, PAGE_SELECTION_MAX_IDS) };
    }

    /** Observable form — rare; most callers want `snapshot()`. */
    get context$(): Observable<PageContext | null> {
        return this._context$.asObservable();
    }

    private _buildContext(url: string): PageContext {
        const leaf = this._deepestSnapshot(this._route.snapshot);
        const ctx: PageContext = { url };
        const componentName = (leaf?.component as { name?: string } | null)?.name;
        if (componentName) { ctx.componentName = componentName; }
        if (leaf?.params && Object.keys(leaf.params).length > 0) {
            ctx.params = { ...leaf.params } as Record<string, string>;
        }
        const title = leaf?.data?.['title'];
        if (typeof title === 'string' && title.length > 0) {
            ctx.title = title;
        }
        return ctx;
    }

    private _deepestSnapshot(snap: ActivatedRouteSnapshot): ActivatedRouteSnapshot | null {
        let cursor: ActivatedRouteSnapshot | null = snap;
        while (cursor?.firstChild) {
            cursor = cursor.firstChild;
        }
        return cursor;
    }
}
